import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { DEFAULT_PAGE_SIZE, getPagedMeta } from '../api/pagination';
import { useDebouncedValue } from './useDebouncedValue';

import type { FetchResult } from '../api/fetchUtils';
import type { PagedResponse, PaginatedListParams } from '../types';

interface UsePaginatedListQueryOptions<T> {
  enabled?: boolean;
  initialPageSize?: number;
  queryKey: (params: PaginatedListParams) => readonly unknown[];
  queryFn: (
    params: PaginatedListParams,
  ) => Promise<FetchResult<PagedResponse<T>>>;
}

const getSearchParam = (queryKey: readonly unknown[]): string => {
  const queryParams = queryKey[queryKey.length - 1];
  if (
    !queryParams ||
    typeof queryParams !== 'object' ||
    !('search' in queryParams)
  ) {
    return '';
  }

  const search = (queryParams as { search?: unknown }).search;
  return typeof search === 'string' ? search : '';
};

export const usePaginatedListQuery = <T>({
  enabled = true,
  initialPageSize = DEFAULT_PAGE_SIZE,
  queryKey,
  queryFn,
}: UsePaginatedListQueryOptions<T>) => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSizeState] = useState(initialPageSize);
  const [searchQuery, setSearchQueryState] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery.trim());

  const params = useMemo<PaginatedListParams>(
    () => ({
      page,
      size: pageSize,
      search: debouncedSearch || undefined,
    }),
    [debouncedSearch, page, pageSize],
  );

  const currentQueryKey = queryKey(params);

  const query = useQuery<PagedResponse<T>>({
    queryKey: currentQueryKey,
    queryFn: async () => {
      const result = await queryFn(params);
      if (!result.success) {
        throw result.error;
      }
      return result.data;
    },
    enabled,
    // Previous data is useful when paging, but can briefly show deleted rows
    // when the user starts a new search. Only retain it for the same search.
    placeholderData: (previousData, previousQuery) =>
      getSearchParam(previousQuery?.queryKey ?? []) ===
      getSearchParam(currentQueryKey)
        ? previousData
        : undefined,
  });

  const setSearchQuery = (queryValue: string) => {
    setPage(0);
    setSearchQueryState(queryValue);
  };

  const setPageSize = (size: number) => {
    setPage(0);
    setPageSizeState(size);
  };

  const meta = getPagedMeta(query.data);

  return {
    ...query,
    items: (query.data?.content ?? []) as T[],
    meta,
    page,
    pageSize,
    searchQuery,
    setPage,
    setPageSize,
    setSearchQuery,
  };
};
